import { useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';

const FORM_URL = 'https://readdy.ai/api/form/d9otrqqftmtnfcfh0e5g';

export default function About() {
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [formError, setFormError] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const honeypot = (form.elements.namedItem('phone_alt') as HTMLInputElement)?.value?.trim();
    if (honeypot) {
      setFormSubmitted(true);
      return;
    }
    const formData = new FormData(form);
    try {
      const res = await fetch(FORM_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const text = await res.text();
      let parsed: Record<string, unknown> | null = null;
      try { parsed = JSON.parse(text); } catch { /* ignore */ }
      if (res.ok && parsed && (parsed as Record<string, string>).code === 'OK') {
        setFormSubmitted(true);
        setFormError('');
        form.reset();
      } else {
        const msg = (parsed as Record<string, unknown>)?.meta
          ? ((parsed as Record<string, unknown>).meta as Record<string, string>).message || ''
          : '';
        setFormError(msg || '提交失败，请稍后重试');
      }
    } catch {
      setFormError('网络错误，请稍后重试');
    }
  };

  return (
    <>
      <Navbar />
      <main className="pt-20 pb-16">
        <div className="px-4 md:px-6 max-w-screen-2xl mx-auto">
          <Breadcrumb
            items={[
              { label: '首页', href: '/' },
              { label: '关于我们' },
            ]}
          />

          {/* Hero */}
          <div className="mt-6 mb-12">
            <h1 className="text-2xl md:text-4xl font-heading font-bold text-foreground-900">
              关于韩漫大全
            </h1>
            <p className="mt-3 text-sm md:text-base text-foreground-600 max-w-3xl leading-relaxed">
              韩漫大全致力于为全球韩漫爱好者提供最优质的在线阅读体验。我们专注于无遮挡下拉式阅读模式，让每一位读者都能沉浸在精彩的韩漫世界中。
            </p>
          </div>

          {/* Brand Story */}
          <section className="mb-14">
            <h2 className="text-xl font-heading font-bold text-foreground-900 mb-6 flex items-center gap-2">
              <i className="ri-book-open-line text-primary-500 w-6 h-6 flex items-center justify-center"></i>
              品牌故事
            </h2>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              <div>
                <p className="text-sm text-foreground-600 leading-relaxed mb-4">
                  韩漫大全创立于2024年，由一群热爱韩国漫画的开发者共同创建。我们发现许多韩漫爱好者在使用传统漫画阅读平台时，常常遇到弹窗广告、翻页卡顿等困扰，严重影响了阅读体验。
                </p>
                <p className="text-sm text-foreground-600 leading-relaxed mb-4">
                  因此，我们打造了韩漫大全——一个专注于下拉式阅读的韩漫平台。我们摒弃了传统的翻页模式，采用无遮挡下拉式设计，让用户像浏览网页一样自然地阅读漫画，真正实现沉浸式体验。
                </p>
                <p className="text-sm text-foreground-600 leading-relaxed">
                  经过两年的发展，韩漫大全已经收录了数百部热门韩国漫画，涵盖恋爱、奇幻、动作、悬疑等多种类型，日均服务数万名韩漫爱好者。未来我们将继续扩展漫画资源，优化阅读体验，成为最受信赖的韩漫在线阅读平台。
                </p>
              </div>
              <div className="bg-background-100 rounded-xl p-6 md:p-8">
                <div className="grid grid-cols-2 gap-6">
                  <div className="text-center">
                    <div className="text-3xl font-heading font-bold text-primary-500">500+</div>
                    <p className="text-xs text-foreground-500 mt-1">收录漫画</p>
                  </div>
                  <div className="text-center">
                    <div className="text-3xl font-heading font-bold text-accent-500">50万+</div>
                    <p className="text-xs text-foreground-500 mt-1">日活跃用户</p>
                  </div>
                  <div className="text-center">
                    <div className="text-3xl font-heading font-bold text-primary-500">10+</div>
                    <p className="text-xs text-foreground-500 mt-1">漫画分类</p>
                  </div>
                  <div className="text-center">
                    <div className="text-3xl font-heading font-bold text-accent-500">99.9%</div>
                    <p className="text-xs text-foreground-500 mt-1">服务可用率</p>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Core Values */}
          <section className="mb-14">
            <h2 className="text-xl font-heading font-bold text-foreground-900 mb-6 flex items-center gap-2">
              <i className="ri-heart-line text-primary-500 w-6 h-6 flex items-center justify-center"></i>
              核心价值
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              <div className="bg-background-100 rounded-xl p-6">
                <div className="w-10 h-10 rounded-lg bg-primary-100 flex items-center justify-center mb-4">
                  <i className="ri-eye-off-line text-xl text-primary-500 w-5 h-5 flex items-center justify-center"></i>
                </div>
                <h3 className="text-sm font-semibold text-foreground-900 mb-2">无遮挡阅读</h3>
                <p className="text-xs text-foreground-600 leading-relaxed">采用下拉式设计，无弹窗、无广告遮挡，让阅读界面干净纯粹，专注享受漫画内容。</p>
              </div>
              <div className="bg-background-100 rounded-xl p-6">
                <div className="w-10 h-10 rounded-lg bg-accent-100 flex items-center justify-center mb-4">
                  <i className="ri-rocket-line text-xl text-accent-500 w-5 h-5 flex items-center justify-center"></i>
                </div>
                <h3 className="text-sm font-semibold text-foreground-900 mb-2">极速加载</h3>
                <p className="text-xs text-foreground-600 leading-relaxed">采用先进的图片预加载和CDN加速技术，确保每一页漫画都能瞬间呈现，告别等待。</p>
              </div>
              <div className="bg-background-100 rounded-xl p-6">
                <div className="w-10 h-10 rounded-lg bg-secondary-100 flex items-center justify-center mb-4">
                  <i className="ri-shield-star-line text-xl text-secondary-500 w-5 h-5 flex items-center justify-center"></i>
                </div>
                <h3 className="text-sm font-semibold text-foreground-900 mb-2">永久免费</h3>
                <p className="text-xs text-foreground-600 leading-relaxed">我们承诺所有漫画内容永久免费开放，无需付费订阅，让每个人都能平等享受韩漫的乐趣。</p>
              </div>
            </div>
          </section>

          {/* Reputation */}
          <section className="mb-14">
            <h2 className="text-xl font-heading font-bold text-foreground-900 mb-6 flex items-center gap-2">
              <i className="ri-star-smile-line text-primary-500 w-6 h-6 flex items-center justify-center"></i>
              用户口碑
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
              <div className="bg-background-100 rounded-xl p-6">
                <div className="flex items-center gap-1 mb-3">
                  {[...Array(5)].map((_, i) => (
                    <i key={i} className="ri-star-fill text-sm text-primary-500 w-4 h-4 flex items-center justify-center"></i>
                  ))}
                </div>
                <p className="text-sm text-foreground-700 leading-relaxed mb-3">
                  "韩漫大全的下拉式阅读体验太棒了！终于不用一直点下一页了，直接滚动就能看完一整话，完全沉浸在剧情里。"
                </p>
                <span className="text-xs text-foreground-500">—— 小明，大学生</span>
              </div>
              <div className="bg-background-100 rounded-xl p-6">
                <div className="flex items-center gap-1 mb-3">
                  {[...Array(5)].map((_, i) => (
                    <i key={i} className="ri-star-fill text-sm text-primary-500 w-4 h-4 flex items-center justify-center"></i>
                  ))}
                </div>
                <p className="text-sm text-foreground-700 leading-relaxed mb-3">
                  "加载速度真的很快，以前在其他平台看漫画总是要等，韩漫大全几乎秒开，追更体验提升了好几个档次。"
                </p>
                <span className="text-xs text-foreground-500">—— 小红，上班族</span>
              </div>
              <div className="bg-background-100 rounded-xl p-6">
                <div className="flex items-center gap-1 mb-3">
                  {[...Array(5)].map((_, i) => (
                    <i key={i} className="ri-star-fill text-sm text-primary-500 w-4 h-4 flex items-center justify-center"></i>
                  ))}
                </div>
                <p className="text-sm text-foreground-700 leading-relaxed mb-3">
                  "资源很全，我想看的韩漫基本都能找到。而且没有任何广告弹窗，这在漫画网站里真的很难得。强烈推荐！"
                </p>
                <span className="text-xs text-foreground-500">—— 阿强，自由职业者</span>
              </div>
            </div>
          </section>

          {/* Contact */}
          <section className="mb-8">
            <h2 className="text-xl font-heading font-bold text-foreground-900 mb-6 flex items-center gap-2">
              <i className="ri-mail-line text-primary-500 w-6 h-6 flex items-center justify-center"></i>
              联系我们
            </h2>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              <div>
                <p className="text-sm text-foreground-600 leading-relaxed mb-6">
                  如果您有任何建议、意见或合作意向，欢迎通过以下方式联系我们。我们会在24小时内回复您的消息。
                </p>
                <div className="space-y-3">
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-lg bg-primary-100 flex items-center justify-center flex-shrink-0">
                      <i className="ri-mail-line text-primary-500 w-4 h-4 flex items-center justify-center"></i>
                    </div>
                    <span className="text-sm text-foreground-700">contact@hanmandq.com</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-lg bg-accent-100 flex items-center justify-center flex-shrink-0">
                      <i className="ri-wechat-line text-accent-500 w-4 h-4 flex items-center justify-center"></i>
                    </div>
                    <span className="text-sm text-foreground-700">微信公众号：韩漫大全</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-lg bg-secondary-100 flex items-center justify-center flex-shrink-0">
                      <i className="ri-global-line text-secondary-500 w-4 h-4 flex items-center justify-center"></i>
                    </div>
                    <span className="text-sm text-foreground-700">https://www.hanmandq.com</span>
                  </div>
                </div>
              </div>

              {/* Contact Form */}
              <div>
                {formSubmitted ? (
                  <div className="bg-accent-100 rounded-xl p-8 text-center">
                    <i className="ri-checkbox-circle-line text-4xl text-accent-500 w-10 h-10 inline-flex items-center justify-center"></i>
                    <p className="mt-3 text-sm font-medium text-foreground-900">感谢您的留言！</p>
                    <p className="text-xs text-foreground-600 mt-1">我们会尽快回复您。</p>
                  </div>
                ) : (
                  <form
                    onSubmit={handleSubmit}
                    data-readdy-form="true"
                    className="bg-background-100 rounded-xl p-6 space-y-4"
                  >
                    {formError && (
                      <div className="text-xs text-red-500 bg-red-50 px-3 py-2 rounded-lg">{formError}</div>
                    )}
                    <div>
                      <label className="block text-xs font-medium text-foreground-700 mb-1.5">姓名</label>
                      <input
                        type="text"
                        name="name"
                        required
                        className="w-full px-3 py-2 bg-background-50 border border-background-200 rounded-lg text-sm text-foreground-900 outline-none focus:border-primary-400 transition-colors"
                        placeholder="请输入您的姓名"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-foreground-700 mb-1.5">邮箱</label>
                      <input
                        type="email"
                        name="email"
                        required
                        className="w-full px-3 py-2 bg-background-50 border border-background-200 rounded-lg text-sm text-foreground-900 outline-none focus:border-primary-400 transition-colors"
                        placeholder="请输入您的邮箱"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-foreground-700 mb-1.5">留言</label>
                      <textarea
                        name="message"
                        required
                        maxLength={500}
                        rows={4}
                        className="w-full px-3 py-2 bg-background-50 border border-background-200 rounded-lg text-sm text-foreground-900 outline-none focus:border-primary-400 transition-colors resize-none"
                        placeholder="请输入您的留言（最多500字）"
                      ></textarea>
                    </div>
                    <input
                      type="text"
                      name="phone_alt"
                      tabIndex={-1}
                      autoComplete="off"
                      aria-hidden="true"
                      readOnly
                      className="honeypot-field"
                    />
                    <button
                      type="submit"
                      className="whitespace-nowrap w-full px-6 py-2.5 bg-primary-500 text-background-50 rounded-full text-sm font-label font-medium hover:bg-primary-600 transition-colors cursor-pointer"
                    >
                      发送留言
                    </button>
                  </form>
                )}
              </div>
            </div>
          </section>
        </div>

        {/* SEO */}
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              '@context': 'https://schema.org',
              '@type': 'AboutPage',
              name: '关于韩漫大全',
              description: '韩漫大全致力于为全球韩漫爱好者提供无遮挡下拉式免费在线阅读体验。了解我们的品牌故事、核心价值和用户口碑。',
              url: window.location.href,
              isPartOf: {
                '@type': 'WebSite',
                name: '韩漫大全',
                url: window.location.origin,
              },
            }),
          }}
        />
      </main>
      <Footer />
    </>
  );
}